import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import DashboardDetailLayout from './DashboardDetailLayout';
import PersonaBadge from '../common/PersonaBadge';
import HealthDot from '../common/HealthDot';
import SuggestionInbox from '../suggestions/SuggestionInbox';
import ActivityTimeline from './persona/ActivityTimeline';
import Portfolio from './persona/Portfolio';
import WorkflowHistory from './persona/WorkflowHistory';
import CapabilitiesProbes from './persona/CapabilitiesProbes';
import LineageNetwork from './persona/LineageNetwork';
import RevisionLog from './persona/RevisionLog';
import api from '../../services/api';

const PersonaDetail: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [persona, setPersona] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    if (!personaId) return;
    // Fetch persona data + analytics in parallel
    Promise.all([
      api.get(`/personas/${personaId}`).then(r => r.data),
      api.get(`/analytics/cross-system/persona/${personaId}/detail`).then(r => r.data),
    ]).then(([p, a]) => {
      setPersona(p);
      setAnalytics(a);
    }).catch(console.error);
  }, [personaId]);

  if (!persona || !personaId) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
        Loading persona...
      </div>
    );
  }

  const name = persona.name || personaId;
  const tags = persona.tags || [];
  const description = persona.description || persona.data?.description || '';
  const isForged = !!persona.data?.data?.engineering_profile;
  const revision = persona.data?.data?.engineering_profile?.current_state?.revision || 0;
  const hasSuggestions = (analytics?.pending_suggestions || 0) > 0;

  return (
    <DashboardDetailLayout
      title={name.replace(/_/g, ' ')}
      subtitle={description}
      backPath="/dashboards/personas"
      badges={
        <div className="flex items-center gap-2">
          <PersonaBadge personaId={personaId} personaName={name} size="lg" revision={revision} health={isForged ? 'green' : 'gray'} clickable={false} />
          {tags.slice(0, 4).map((t: string) => (
            <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-purple-600/20 text-purple-300">{t}</span>
          ))}
          <HealthDot status={isForged ? 'green' : 'gray'} reason={isForged ? 'Forged persona' : 'Legacy import'} size="md" />
        </div>
      }
      actions={
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/personas/forge?personaId=${personaId}`)}
            className="px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700"
          >
            Open in Forge
          </button>
          <button
            onClick={() => navigate(`/personas/forge`, { state: { remixPersonaId: personaId } })}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
            }`}
          >
            Remix
          </button>
          <button
            onClick={() => navigate(`/personas/forge?personaId=${personaId}&tab=3`)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
            }`}
          >
            View Probes
          </button>
        </div>
      }
      tabs={[
        { key: 'activity', label: 'Activity Timeline', content: <ActivityTimeline personaId={personaId} /> },
        { key: 'portfolio', label: 'Portfolio', content: <Portfolio personaId={personaId} /> },
        { key: 'workflows', label: 'Workflow History', content: <WorkflowHistory personaId={personaId} /> },
        { key: 'capabilities', label: 'Capabilities & Probes', content: <CapabilitiesProbes personaId={personaId} persona={persona} /> },
        { key: 'lineage', label: 'Heritage & Network', content: <LineageNetwork personaId={personaId} /> },
        { key: 'revisions', label: 'Revision Log', content: <RevisionLog personaId={personaId} persona={persona} /> },
      ]}
      sidebar={hasSuggestions ? <SuggestionInbox personaId={personaId} /> : undefined}
    />
  );
};

export default PersonaDetail;
